import { Grid } from "@mui/material";
import * as React from "react";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as taskService from "../../../models/services/taskService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode } from "../../../models/unit/unitModuleNode";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { UnitModuleSettings } from "../../unit/Unit/UnitModuleSettings";
import { TaskPriorityList } from "./TaskPriorityList";
import { TaskStateList } from "./TaskStateList";
import { TaskTypeGrid } from "./TaskTypeGrid";
import { TaskTypesByUnitsDialog } from "./TaskTypesByUnitsDialog";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";

/* eslint-disable no-unused-vars */
export const enum TaskSettingsDialogMenuItem {
    Units = "1",
    Permissions = "2",
    TaskStates = "3",
    TaskPriorities = "4",
    CompanyTaskTypes = "5",
    DefaultTaskStates = "6",
    DefaultTaskPriorities = "7",
    DefaultTaskTypes = "8"
}
/* eslint-enable no-unused-vars */

//TaskSettingsDialog
export interface ITaskSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    settingsActions: IPageNavMenuItem[];
    onClose: () => void;
}

export interface ITaskSettingsDialogState {
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;
    showTaskTypesByUnitsDialog: boolean;
    activeMenuItem: TaskSettingsDialogMenuItem;
}

export class TaskSettingsDialog extends React.Component<ITaskSettingsDialogProp, ITaskSettingsDialogState> {
    private unitsRef = React.createRef<UnitModuleSettings>();
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();

    constructor(props: ITaskSettingsDialogProp) {
        super(props);
        this.state = {
            unitModuleNode: null,
            unitNodePermissions: null,
            showTaskTypesByUnitsDialog: false,
            activeMenuItem: props.settingsActions.findIndex(item => item.id === TaskSettingsDialogMenuItem.Units) !== -1
                ? TaskSettingsDialogMenuItem.Units
                : TaskSettingsDialogMenuItem.TaskStates,
        };
    }

    async componentDidMount(): Promise<void> {
        const state = this.state;
        if (state.activeMenuItem === TaskSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        }
    }

    // #region Units
    onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await this.unitsRef.current.handleCancel(false);
    };

    loadUnits = async () => {
        const result = await AppUtils.callService(() => taskService.getUnits(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitModuleNode: result.unitModuleNode
        });
    };

    handleUnitModuleSettingsSave = async (obj: any) => {
        return await taskService.saveUnits(obj);
    };
    // #endregion Units

    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => taskService.getUnitNodePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await taskService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    handleSubViewOk = (_closeDialog: boolean) => {
    };

    handleToggleActiveMenuItem = async (menuItem: TaskSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === TaskSettingsDialogMenuItem.Units) {
            if (!await this.onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === TaskSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === TaskSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        } else if (menuItem === TaskSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleShowTaskTypesByUnitsDialog = () => {
        this.setState({
            showTaskTypesByUnitsDialog: true
        });
    };

    handleCloseTaskTypesByUnitsDialog = () => {
        this.setState({
            showTaskTypesByUnitsDialog: false
        });
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose();
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <Dialog
                    id="taskSettingsDialog"
                    title={Translations.Settings}
                    maxWidth="lg"
                    maxHeight="100%"
                    onClose={() => this.handleCancel(true)}
                    body={
                        <GridRow flexGrow={2}>
                            <Grid item sm={3}>
                                <PageNavMenuItems
                                    activeId={state.activeMenuItem}
                                    items={props.settingsActions}
                                    onToggleMenuItem={this.handleToggleActiveMenuItem}
                                />
                            </Grid>
                            <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                                {(state.activeMenuItem === TaskSettingsDialogMenuItem.Units) && (
                                    <UnitModuleSettings
                                        ref={this.unitsRef}
                                        companyId={props.companyId}
                                        sessionState={props.sessionState}
                                        applicationPermissionType={ApplicationPermissionType.Task}
                                        unitModuleNode={state.unitModuleNode}
                                        onCancel={this.handleCancel}
                                        onOk={this.handleSubViewOk}
                                        onSave={this.handleUnitModuleSettingsSave}
                                    />
                                )}
                                {state.activeMenuItem === TaskSettingsDialogMenuItem.Permissions && (
                                    <UnitPermissionSettingsNew
                                        ref={this.permissionsRef}
                                        infoText={Translations.TaskModuleInfoPermissions}
                                        companyId={props.companyId}
                                        unitNodePermissions={state.unitNodePermissions}
                                        cardView={true}
                                        onSave={this.handleUnitPermissionSettingsSave}
                                        onOk={this.handleSubViewOk}
                                        onCancel={this.handleCancel}
                                    />
                                )}
                                {(state.activeMenuItem === TaskSettingsDialogMenuItem.TaskStates || state.activeMenuItem === TaskSettingsDialogMenuItem.DefaultTaskStates) &&
                                    <TaskStateList
                                        companyId={state.activeMenuItem === TaskSettingsDialogMenuItem.TaskStates ? props.companyId : null}
                                    />
                                }
                                {(state.activeMenuItem === TaskSettingsDialogMenuItem.TaskPriorities || state.activeMenuItem === TaskSettingsDialogMenuItem.DefaultTaskPriorities) &&
                                    <TaskPriorityList
                                        companyId={state.activeMenuItem === TaskSettingsDialogMenuItem.TaskPriorities ? props.companyId : null}
                                    />
                                }
                                {(state.activeMenuItem === TaskSettingsDialogMenuItem.CompanyTaskTypes || state.activeMenuItem === TaskSettingsDialogMenuItem.DefaultTaskTypes) &&
                                    <TaskTypeGrid
                                        companyId={state.activeMenuItem === TaskSettingsDialogMenuItem.CompanyTaskTypes ? props.companyId : null}
                                        sessionState={props.sessionState}
                                    />
                                }
                            </Grid>
                        </GridRow>
                    }
                    buttons={
                        state.activeMenuItem !== TaskSettingsDialogMenuItem.Units && state.activeMenuItem !== TaskSettingsDialogMenuItem.Permissions
                            ? (state.activeMenuItem === TaskSettingsDialogMenuItem.CompanyTaskTypes
                                ? [{ title: Translations.TaskTypesByUnits, variant: "confirmation", margin: "0 auto 0 25.4%", onClick: () => this.handleShowTaskTypesByUnitsDialog() }, { title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                                : [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                            )
                            : null
                    }
                />
                {state.showTaskTypesByUnitsDialog &&
                    <TaskTypesByUnitsDialog
                        companyId={props.companyId}
                        onCancel={this.handleCloseTaskTypesByUnitsDialog}
                    />
                }
            </>
        );
    }
}